import { useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { grantApi } from '../lib/api'
import { getAllStopsDoneHere, localDateKey } from '../lib/offlineStore'
import { todayCards } from '../lib/todayAssignments'
import { useMyTurfs } from './useCanvasser'

/** [CF-1 P3] Today's assignment cards: server order/labels, progress from the phone's own walk data. */
export function useToday(campaignId: string | undefined) {
  const qc = useQueryClient()
  const turfsQ = useMyTurfs(campaignId)
  const todayQ = useQuery({
    queryKey: ['canvasser', 'today', campaignId],
    queryFn: () => grantApi.getToday().then(r => r.data),
    enabled: !!campaignId,
    networkMode: 'offlineFirst',
    // [CF-1 P4] Counts move while Today is unmounted (a call list drains in the background): re-read on
    // every mount rather than serve the 5-minute cache. A failed refetch keeps the last good list.
    refetchOnMount: 'always',
    retry: false,
  })
  const doneQ = useQuery({
    queryKey: ['canvasser', 'doneHere', 'all', localDateKey()],
    queryFn: () => getAllStopsDoneHere(),
    networkMode: 'always',
    staleTime: Infinity,
  })
  const turfs = turfsQ.data
  // A failed background refetch keeps the last good list; a failure with no data is already null.
  const server = todayQ.data ?? null
  const doneHere = doneQ.data
  const cards = useMemo(
    () => (turfs ? todayCards(server, turfs, doneHere ?? new Map()) : []),
    [turfs, server, doneHere],
  )
  // [m5] The server names a turf the phone doesn't hold (assigned after my-turfs was cached): refetch
  // my-turfs ONCE per distinct missing set, so the new card appears without looping on a turf the
  // server keeps listing but my-turfs never returns.
  const askedFor = useRef<string | null>(null)
  const missingKey = useMemo(() => {
    if (!server || !turfs) return ''
    const held = new Set(turfs.map(t => t.turfId))
    return [...new Set(server.flatMap(e => (e.turfId && !held.has(e.turfId) ? [e.turfId] : [])))].sort().join(',')
  }, [server, turfs])
  useEffect(() => {
    if (!missingKey || askedFor.current === missingKey) return
    askedFor.current = missingKey
    void qc.invalidateQueries({ queryKey: ['canvasser', 'turfs'] })
  }, [missingKey, qc])

  // A failed Today fetch is not a loading state; only turfs gate the list.
  const todayPending = todayQ.isLoading && todayQ.fetchStatus !== 'idle'
  return { cards, isLoading: turfsQ.isLoading || todayPending, error: turfsQ.error }
}
